import React, { useRef, useState, useEffect } from 'react';
import { FiX, FiCheck, FiTrash2 } from 'react-icons/fi';

const SignatureCanvas = ({ isOpen, onClose, onSave, title = "Semnează" }) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [context, setContext] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Initialize canvas
    useEffect(() => {
        if (!isOpen) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 3;

        // Set white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        setContext(ctx);
    }, [isOpen]);

    const startDrawing = (e) => {
        if (!context) return;

        const { offsetX, offsetY } = getCoordinates(e);
        context.beginPath();
        context.moveTo(offsetX, offsetY);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing || !context) return;

        const { offsetX, offsetY } = getCoordinates(e);
        context.lineTo(offsetX, offsetY);
        context.stroke();
    };

    const stopDrawing = () => {
        if (!context) return;

        context.closePath();
        setIsDrawing(false);
    };

    // Get coordinates for touch and mouse events
    const getCoordinates = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        if (e.type.startsWith('touch')) {
            const touch = e.touches[0] || e.changedTouches[0];
            return {
                offsetX: touch.clientX - rect.left,
                offsetY: touch.clientY - rect.top
            };
        } else {
            return {
                offsetX: e.clientX - rect.left,
                offsetY: e.clientY - rect.top
            };
        }
    };

    const clearCanvas = () => {
        if (!context || !canvasRef.current) return;

        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    };

    const handleSave = async () => {
        if (!canvasRef.current) return;

        try {
            setIsSaving(true);
            const canvas = canvasRef.current;
            const imageDataURL = canvas.toDataURL('image/png');

            if (onSave) {
                await onSave(imageDataURL);
            }

        } catch (error) {
            console.error('Error saving signature:', error);
            alert('A apărut o eroare la salvarea semnăturii. Vă rugăm să încercați din nou.');
        } finally {
            setIsSaving(false);
        }
    };

    // Adjust canvas size to container size
    useEffect(() => {
        const resizeCanvas = () => {
            if (!canvasRef.current || !isOpen) return;

            const container = canvasRef.current.parentElement;
            if (!container) return;

            const canvas = canvasRef.current;
            canvas.width = Math.min(container.clientWidth - 40, 600);
            canvas.height = 300;

            // Redraw white background
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Restore context settings
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 3;

            setContext(ctx);
        };

        if (isOpen) {
            resizeCanvas();
            window.addEventListener('resize', resizeCanvas);
        }

        return () => {
            window.removeEventListener('resize', resizeCanvas);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1200,
            padding: '20px'
        }}>
            <div style={{
                backgroundColor: '#f5f5f5',
                borderRadius: '10px',
                width: '100%',
                maxWidth: '700px',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                maxHeight: '90vh',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <div style={{
                    padding: '15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#26C9BC',
                    color: 'white'
                }}>
                    <h2 style={{ margin: 0, fontSize: '1.5rem' }}>{title}</h2>
                    <button
                        style={{
                            backgroundColor: 'transparent',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '30px',
                            height: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '20px'
                        }}
                        onClick={onClose}
                    >
                        <FiX />
                    </button>
                </div>

                {/* Instructions */}
                <div style={{
                    padding: '15px',
                    backgroundColor: '#ffffff',
                    borderBottom: '1px solid #eeeeee',
                    textAlign: 'center',
                    color: '#666'
                }}>
                    Desenează semnătura ta în spațiul de mai jos folosind mouse-ul sau degetul
                </div>

                {/* Canvas Container */}
                <div style={{
                    padding: '20px',
                    display: 'flex',
                    justifyContent: 'center',
                    backgroundColor: '#ffffff',
                    overflow: 'hidden',
                    flex: 1
                }}>
                    <canvas
                        ref={canvasRef}
                        style={{
                            border: '2px solid #ddd',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                            touchAction: 'none',
                            cursor: 'crosshair'
                        }}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseOut={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                    />
                </div>

                {/* Footer */}
                <div style={{
                    padding: '15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: '1px solid #eeeeee',
                    backgroundColor: '#ffffff',
                    gap: '10px'
                }}>
                    <button
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '10px 20px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: '500',
                            transition: 'background-color 0.2s'
                        }}
                        onClick={clearCanvas}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#d32f2f'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#f44336'}
                    >
                        <FiTrash2 />
                        <span>Șterge</span>
                    </button>

                    <button
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: '#26C9BC',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '10px 20px',
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            fontWeight: '500',
                            transition: 'background-color 0.2s',
                            opacity: isSaving ? 0.6 : 1
                        }}
                        onClick={handleSave}
                        disabled={isSaving}
                        onMouseOver={(e) => !isSaving && (e.target.style.backgroundColor = '#1db2a6')}
                        onMouseOut={(e) => !isSaving && (e.target.style.backgroundColor = '#26C9BC')}
                    >
                        <FiCheck />
                        <span>{isSaving ? 'Se salvează...' : 'Salvează'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SignatureCanvas;